import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tooltip, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { supabase } from '../config/supabase';

type Preview = {
  previewId: string;
  message: string;
  teamName: string;
  matchLabel: string;
  destination: string;
  alreadyPublished: boolean;
  alreadySent: boolean;
};

async function api(path: string, body?: Record<string, unknown>) {
  const { data } = await supabase.auth.getSession();
  const response = await fetch(path, {
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(data.session?.access_token ? { Authorization: `Bearer ${data.session.access_token}` } : {}),
    },
  });
  const result = await response.json() as Record<string, unknown>;
  return { response, result };
}

export function GroupMeLineupSend({ matchId, disabled = false, disabledReason, onSent }: {
  matchId: string;
  disabled?: boolean;
  disabledReason?: string;
  onSent?: (message: string) => void | Promise<void>;
}) {
  const [configured, setConfigured] = useState(false);
  const [reason, setReason] = useState('Checking GroupMe configuration…');
  const [recoveryId, setRecoveryId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [repostConfirmed, setRepostConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setConfigured(false); setReason('Checking GroupMe configuration…'); setRecoveryId(null);
    void api(`/api/groupme/lineup/status?matchId=${encodeURIComponent(matchId)}`).then(({ response, result }) => {
      if (cancelled) return;
      setConfigured(response.ok && result.configured === true);
      setReason(String(result.reason || (response.ok ? '' : result.error || 'Unable to check GroupMe configuration.')));
      setRecoveryId(typeof result.recoveryPreviewId === 'string' ? result.recoveryPreviewId : null);
    }).catch(() => { if (!cancelled) setReason('Unable to check GroupMe configuration.'); });
    return () => { cancelled = true; };
  }, [matchId]);

  const prepare = async () => {
    setOpen(true); setBusy(true); setError(''); setRepostConfirmed(false);
    try {
      const { response, result } = await api('/api/groupme/lineup/preview', { matchId });
      if (!response.ok) throw new Error(String(result.error || 'Unable to preview lineup.'));
      setPreview(result as Preview);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to preview lineup.'); }
    finally { setBusy(false); }
  };

  const send = async (previewId: string, confirmRepost = false) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const { response, result } = await api('/api/groupme/lineup/send', { previewId, confirmRepost });
      if (result.confirmationRequired) { setRepostConfirmed(true); setError(String(result.error || 'Confirm the repost.')); return; }
      if (!response.ok) {
        if (result.recoveryRequired) setRecoveryId(previewId);
        throw new Error(String(result.error || 'Unable to send lineup.'));
      }
      setOpen(false); setPreview(null); setRecoveryId(null);
      await onSent?.(String(result.message || 'Lineup sent to GroupMe and published.'));
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to send lineup.'); }
    finally { setBusy(false); }
  };

  const unavailable = disabled || (!configured && !recoveryId);
  const tooltip = disabled ? disabledReason || 'Complete lineup validation before sending.' : reason || 'Send this saved lineup to GroupMe';
  return <>
    <Tooltip title={unavailable ? tooltip : 'Preview and send the saved lineup to GroupMe'}><span>
      <Button variant="outlined" startIcon={<SendIcon />} disabled={unavailable || busy} onClick={() => recoveryId ? void send(recoveryId) : void prepare()}>
        {recoveryId ? 'Complete GroupMe Publication' : 'Send Lineup to GroupMe'}
      </Button>
    </span></Tooltip>
    <Dialog open={open} onClose={busy ? undefined : () => { setOpen(false); setPreview(null); }} fullWidth maxWidth="sm">
      <DialogTitle>Send Lineup to GroupMe</DialogTitle>
      <DialogContent>
        {busy && !preview && <CircularProgress size={24} />}
        {preview && <Stack spacing={1.25} sx={{ pt: 1 }}>
          <Typography variant="body2"><strong>Team:</strong> {preview.teamName}</Typography>
          <Typography variant="body2"><strong>Match:</strong> {preview.matchLabel}</Typography>
          <Typography variant="body2"><strong>Destination:</strong> {preview.destination}</Typography>
          <Typography variant="subtitle2">Message Preview</Typography>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '45vh', overflowY: 'auto', m: 0, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, fontFamily: 'inherit', fontSize: '0.875rem' }}>{preview.message}</Box>
          <Typography variant="body2" color="text.secondary">{preview.alreadyPublished ? 'This will repost the lineup. It is already Published in CourtCaptain.' : 'Sending this lineup to GroupMe will also publish it in CourtCaptain.'}</Typography>
          {preview.alreadySent && <Alert severity="warning">This exact lineup version was already sent to this GroupMe group. Confirm before reposting.</Alert>}
        </Stack>}
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button disabled={busy} onClick={() => { setOpen(false); setPreview(null); }}>Cancel</Button>
        <Button variant="contained" disabled={busy || !preview} onClick={() => preview && void send(preview.previewId, repostConfirmed || preview.alreadySent)}>
          {busy ? 'Sending…' : preview?.alreadyPublished ? 'Send to GroupMe' : preview?.alreadySent ? 'Confirm Repost & Publish' : 'Send & Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}
